import './timeregistration-user-registrations-modal-entry-item.css';

import { useLogs } from '../../../logsContext';
import TIME_LOG_ENTRY_TYPES from '../../../constants/time/timeLogEntryTypes';
import { useState, useCallback, useContext } from 'react';
import { convertTimeOffTypeUUIDToName } from '../../../views/timeregistration/timeregistration-utils';
import InputField from '../../input-field/input-field';
import { Timestamp } from 'firebase/firestore';
import Button from '../../button/button';
import { UserInfoContext, EmployeesContext } from '../../../context';
import { currentTimeLogSetting } from '../../../utils/currentTimeLogSetting';

function EntryItem({ modalEmployeeId, modalDate, entry, timeOffTypes }) {
  const [editMode, setEditMode] = useState(false);
  const [includeBreak, setIncludeBreak] = useState(
    entry?.breakWasIncluded || false
  );
  const [formValues, setFormValues] = useState({
    hours: entry.hours,
    type: entry.type,
    note: entry?.note || '',
    startTime: entry.startTime,
    timeOffType: entry?.timeOffType || '',
    endTime: entry.endTime,
    breakWasIncluded: entry?.breakWasIncluded || false,
  });
  const [valuesChanged, setValuesChanged] = useState(false);

  const { updateLogEntry, deleteLogEntry } = useLogs();
  const userInfo = useContext(UserInfoContext);
  const employees = useContext(EmployeesContext);

  const isNegative =
    entry.type === 'sickLeave' ||
    entry.type === 'holiday' ||
    entry.type === 'timeOff';

  // Find out type for entry.startTime
  const startTime = new Date(entry.startTime.seconds * 1000).toLocaleTimeString(
    'da-DK',
    {
      timeStyle: 'short',
    }
  );
  const endTime = new Date(entry.endTime.seconds * 1000).toLocaleTimeString(
    'da-DK',
    {
      timeStyle: 'short',
    }
  );

  const handleEditMode = () => {
    if (editMode) {
      return;
    }
    setEditMode(!editMode);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    // Check if value has changed
    if (formValues[name] === value) {
      setValuesChanged(false);
    } else {
      setValuesChanged(true);
    }

    if (name === 'endTime' || name === 'startTime') {
      // Use modalDate as the base date instead of preserving the existing date
      const date = new Date(modalDate);

      // Set the hours and minutes from 'value'
      const newTime = new Date(value);
      date.setHours(newTime.getHours());
      date.setMinutes(newTime.getMinutes());

      // Move to nearest 15 minutes, e.g. 12:07 -> 12:15 or 12:23 -> 12:30
      const minutesRounded = Math.ceil(date.getMinutes() / 15) * 15;
      date.setMinutes(minutesRounded);

      // Calculate hours
      if (name === 'endTime' && formValues.startTime) {
        const start = new Date(formValues.startTime.seconds * 1000).getTime();
        const end = date.getTime();
        let calculatedHours = (end - start) / 36e5;

        // Apply break adjustment if break is included
        if (includeBreak) {
          const targetUserInfo = modalEmployeeId
            ? employees.find((emp) => emp.id === modalEmployeeId)?.data
            : userInfo;

          if (targetUserInfo && userInfo?.companyData) {
            const timeLogSetting = currentTimeLogSetting(
              userInfo.companyData,
              targetUserInfo,
              new Date(formValues.startTime.seconds * 1000)
            );

            if (
              timeLogSetting?.breakDuration > 0 &&
              !timeLogSetting?.paidBreak
            ) {
              calculatedHours -= timeLogSetting.breakDuration / 60;
            }
          }
        }

        setFormValues({
          ...formValues,
          [name]: Timestamp.fromDate(date),
          hours: calculatedHours,
          breakWasIncluded: includeBreak,
        });
      } else if (name === 'startTime' && formValues.endTime) {
        const start = date.getTime();
        const end = new Date(formValues.endTime.seconds * 1000).getTime();
        let calculatedHours = (end - start) / 36e5;

        // Apply break adjustment if break is included
        if (includeBreak) {
          const targetUserInfo = modalEmployeeId
            ? employees.find((emp) => emp.id === modalEmployeeId)?.data
            : userInfo;

          if (targetUserInfo && userInfo?.companyData) {
            const timeLogSetting = currentTimeLogSetting(
              userInfo.companyData,
              targetUserInfo,
              date
            );

            if (
              timeLogSetting?.breakDuration > 0 &&
              !timeLogSetting?.paidBreak
            ) {
              calculatedHours -= timeLogSetting.breakDuration / 60;
            }
          }
        }

        setFormValues({
          ...formValues,
          [name]: Timestamp.fromDate(date),
          hours: calculatedHours,
          breakWasIncluded: includeBreak,
        });
      } else {
        setFormValues({
          ...formValues,
          [name]: Timestamp.fromDate(date),
          breakWasIncluded: includeBreak,
        });
      }
    } else {
      // Save as string
      setFormValues({ ...formValues, [name]: value });
    }
  };

  const handleIncludeBreakChange = (value) => {
    setIncludeBreak(value);

    const targetUserInfo = modalEmployeeId
      ? employees.find((emp) => emp.id === modalEmployeeId)?.data
      : userInfo;
    if (!targetUserInfo) return;

    // Get current time log setting
    const timeLogSetting = currentTimeLogSetting(
      userInfo.companyData,
      targetUserInfo,
      new Date(formValues.startTime.toDate())
    );

    // Calculate hours based on time difference
    const start = formValues.startTime.toMillis();
    const end = formValues.endTime.toMillis();
    let calculatedHours = (end - start) / 36e5; // Convert to hours

    // If break IS included (true), subtract break time from total hours
    // If break is NOT included (false), use the full time difference
    if (
      value &&
      timeLogSetting?.breakDuration > 0 &&
      !timeLogSetting?.paidBreak
    ) {
      calculatedHours -= timeLogSetting.breakDuration / 60; // Convert minutes to hours
    }

    setFormValues({
      ...formValues,
      hours: calculatedHours,
      breakWasIncluded: value,
    });

    setValuesChanged(true);
  };

  const handleDelete = useCallback(async () => {
    if (!window.confirm('Er du sikker på, at du vil slette registreringen?')) {
      return;
    }

    try {
      // Call deleteLogEntry with userId, date, and entryId
      await deleteLogEntry(modalEmployeeId, modalDate, entry.id);

      // Exit edit mode or handle UI changes accordingly
      setEditMode(false);
    } catch (error) {
      console.error('Error deleting log entry:', error);
      alert('Failed to delete log entry. Please try again.');
    }
  }, [deleteLogEntry, modalEmployeeId, modalDate, entry.id, setEditMode]);

  const handleSubmit = useCallback(async () => {
    const valuesToSave = {
      ...formValues,
      date: Timestamp.fromDate(new Date(modalDate)),
      startTime: Timestamp.fromDate(
        new Date(formValues.startTime.seconds * 1000)
      ),
      endTime: Timestamp.fromDate(new Date(formValues.endTime.seconds * 1000)),
      breakWasIncluded: includeBreak,
    };

    await updateLogEntry(modalEmployeeId, modalDate, entry.id, valuesToSave);

    setEditMode(false);
  }, [
    formValues,
    modalEmployeeId,
    modalDate,
    updateLogEntry,
    entry.id,
    includeBreak,
  ]);
  return (
    <div
      className="timeregistration-user-registrations-modal-entry-item"
      onClick={handleEditMode}
    >
      {editMode ? (
        <>
          <div className="timeregistration-user-registrations-modal-entry-item-form-row">
            <InputField
              name="type"
              type="select"
              label="Type"
              placeholder="- Vælg -"
              options={TIME_LOG_ENTRY_TYPES.map((type) => ({
                value: type.value,
                label: type.label,
              }))}
              value={formValues.type}
              onChange={handleChange}
              className={`timeregistration-user-registrations-modal-entry-item-form__type ${
                formValues.type === 'timeOff'
                  ? 'timeregistration-user-registrations-modal-entry-item-form__type--timeOff'
                  : ''
              }`}
            />
            {formValues.type === 'timeOff' && (
              <InputField
                name="timeOffType"
                type="select"
                label="Årsag"
                placeholder="- Vælg -"
                options={timeOffTypes}
                value={formValues.timeOffType}
                onChange={handleChange}
                className={`timeregistration-user-registrations-modal-entry-item-form__type ${
                  formValues.type === 'timeOff'
                    ? 'timeregistration-user-registrations-modal-entry-item-form__type--timeOff'
                    : ''
                }`}
              />
            )}
          </div>

          {/* Toggle for "include break" - only show if not hourly paid and not overtime */}
          {(() => {
            // Don't show break toggle for overtime entries
            if (formValues.type === 'overtime') {
              return null;
            }

            // Check if the employee is hourly paid
            const targetUserInfo = modalEmployeeId
              ? employees.find((emp) => emp.id === modalEmployeeId)?.data
              : userInfo;

            if (!targetUserInfo || !userInfo?.companyData) {
              return null;
            }

            const timeLogSetting = currentTimeLogSetting(
              userInfo.companyData,
              targetUserInfo,
              new Date(formValues.startTime.toDate())
            );

            // Don't show break toggle if hourly paid (hourly paid employees don't have paid breaks)
            if (timeLogSetting?.isPaidHourly || timeLogSetting?.paidBreak) {
              return null;
            }

            return (
              <div className="timeregistration-user-registrations-modal-entry-item-form-row">
                <InputField
                  name="includeBreak"
                  type="switch"
                  label="Medregn pause"
                  showLabel
                  checked={includeBreak}
                  onChange={() => handleIncludeBreakChange(!includeBreak)}
                  className="timeregistration-user-registrations-modal-entry-item-form__switch"
                />
              </div>
            );
          })()}

          <div className="timeregistration-user-registrations-modal-entry-item-form-row">
            <InputField
              name="startTime"
              type="time"
              label="Fra"
              placeholder="Fra"
              value={formValues.startTime.toDate()}
              onChange={handleChange}
              className="timeregistration-user-registrations-modal-entry-item-form__time"
            />
            <InputField
              name="endTime"
              type="time"
              label="Til"
              placeholder="Til"
              value={formValues.endTime.toDate()}
              onChange={handleChange}
              className="timeregistration-user-registrations-modal-entry-item-form__time"
            />
            <InputField
              name="hours"
              label="Timer"
              type="number"
              key={`hours-${formValues.hours}`}
              value={formValues.hours}
              disabled
              className="timeregistration-user-registrations-modal-entry-item-form__hours"
            />
          </div>
          <InputField
            name="note"
            label="Note"
            type="text"
            onChange={handleChange}
            value={formValues.note || ''}
          />
          <div className="timeregistration-user-registrations-modal-entry-item-form__buttons">
            <Button
              buttonStyle="secondary"
              onClick={handleDelete}
              className="timeregistration-user-registrations-modal-entry-item-form__buttons--delete"
            >
              Slet
            </Button>
            <Button
              buttonStyle="text-link"
              onClick={() => {
                setEditMode(false);
              }}
            >
              Luk
            </Button>
            <Button onClick={handleSubmit} disabled={!valuesChanged}>
              Gem
            </Button>
          </div>
        </>
      ) : (
        <>
          <span className="timeregistration-user-registrations-modal-entry-item__type">
            {
              TIME_LOG_ENTRY_TYPES.find((type) => type.value === entry.type)
                ?.label
            }
            {entry.timeOffType &&
              ` · ${convertTimeOffTypeUUIDToName(
                entry.timeOffType,
                timeOffTypes
              )}`}
          </span>
          <div className="timeregistration-user-registrations-modal-entry-item-inner">
            <span className="timeregistration-user-registrations-modal-entry-item__time">
              {startTime}
              {' - '}
              {endTime}
              {entry.breakWasIncluded && (
                <span className="timeregistration-user-registrations-modal-entry-item__break-indicator">
                  {' (inkl. pause)'}
                </span>
              )}
            </span>
            <span
              className={`timeregistration-user-registrations-modal-entry-item__hours ${
                isNegative
                  ? 'timeregistration-user-registrations-modal-entry-item__hours--negative'
                  : ''
              }`}
            >
              {isNegative ? '-' : '+'}
              {entry.hours.toLocaleString('da-DK', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}{' '}
              timer
            </span>
          </div>
          {entry.note && (
            <span className="timeregistration-user-registrations-modal-entry-item__note">
              {entry.note}
            </span>
          )}
        </>
      )}
    </div>
  );
}

export default EntryItem;
